import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { generateImage } from '../services/ideogramService';
import magic from '../lib/magic'; // Use the regular Magic SDK instance
import { templates, getTemplatePrompt, generateStoryText } from '../services/templateService';
import { debounce } from 'lodash'; // Add this import
import { generateReplicateImage } from '../services/replicateService';
import { useShopping } from '../contexts/ShoppingContext';
import CartDrawer from '../components/CartDrawer';

const AI_MODELS = [
  {
    id: 'ideogram',
    name: 'Ideogram',
    description: 'High-quality artistic images with excellent composition'
  },
  {
    id: 'recraft-svg',
    name: 'Recraft SVG',
    description: 'Vector-based SVG illustrations with clean, modern style',
    enabled: true
  },
  {
    id: 'recraft-photo',
    name: 'Recraft Photo',
    description: 'Realistic photo-style images with high detail',
    enabled: true
  },
  {
    id: 'flux',
    name: 'Flux',
    description: 'Coming soon - Next-gen AI imaging',
    enabled: false
  }
];

const TEMPLATE_MODEL_MAPPING = {
  'line_art': 'recraft-svg' // Add more mappings here if needed
};

const Generator = () => {
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImages, setGeneratedImages] = useState(() => {
    // Load from localStorage on initial render
    const saved = localStorage.getItem('generatedImages');
    return saved ? JSON.parse(saved) : [];
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { 
    credits, 
    plan, 
    useCredit: spendCredit, 
    logout, 
    user, 
    fetchUserCredits,
    setCredits
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isVerifyingPayment, setIsVerifyingPayment] = useState(false);
  const [expandedPrompt, setExpandedPrompt] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isTemplateMenuOpen, setIsTemplateMenuOpen] = useState(false);
  const [drawingAge, setDrawingAge] = useState(10); // Default age
  const [debouncedPrompt, setDebouncedPrompt] = useState('');
  const [storyPanel, setStoryPanel] = useState(1);
  const [storyImages, setStoryImages] = useState([]);
  const [isGeneratingStory, setIsGeneratingStory] = useState(false);
  const [storyProgress, setStoryProgress] = useState({
    stage: '',
    progress: 0,
    currentChapter: 0,
    totalChapters: 6
  });
  const [selectedAiModel, setSelectedAiModel] = useState({ id: 'ideogram', name: 'Ideogram' });
  const [isAiModelMenuOpen, setIsAiModelMenuOpen] = useState(false);
  const [isTemplateDropdownOpen, setIsTemplateDropdownOpen] = useState(false);
  const [isAiModelDropdownOpen, setIsAiModelDropdownOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(null); // null, 'template', 'aiModel', 'mobile'
  const { 
    selectedImages, 
    toggleImageSelection, 
    addToCart, 
    clearSelected,
    cartItems,
    isCartOpen,
    setCartItems,
    setIsCartOpen
  } = useShopping();

  // Save to localStorage whenever generatedImages changes
  useEffect(() => {
    localStorage.setItem('generatedImages', JSON.stringify(generatedImages));
  }, [generatedImages]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const sessionId = queryParams.get('session_id');
    const success = queryParams.get('success');

    if (sessionId && success) {
      setIsVerifyingPayment(true);
      // Fetch updated credits
      fetchUserCredits(user.email)
        .then(() => {
          setIsVerifyingPayment(false);
          // Clear the URL without triggering a refresh
          window.history.replaceState({}, '', '/generator');
        })
        .catch((error) => {
          console.error('Error verifying payment:', error);
          setIsVerifyingPayment(false);
        })
        .finally(() => {
          setTimeout(() => {
            setIsVerifyingPayment(false);
          }, 5000);
        });
    }
  }, [location, user, fetchUserCredits]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (window.innerWidth >= 768) { // md breakpoint
        if (!event.target.closest('.relative')) {
          setMenuOpen(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedTemplate?.hasStoryMode) {
      if (credits < 6) {
        setError("You need at least 6 credits to generate a story. Please upgrade your plan.");
        return;
      }
      generateStoryPanel();
      return;
    }
    
    if (!prompt.trim()) return;

    if (credits <= 0) {
      setError("You've run out of credits! Please upgrade your plan to continue generating images.");
      return;
    }

    setError('');
    setIsGenerating(true);
    let creditUsed = false;
    
    try {
      // Get the DID token
      const didToken = await magic.user.getIdToken();
      console.log('🔑 Got DID token');
      
      // Use a credit first
      console.log('💳 Attempting to use credit...', { currentCredits: credits });
      const creditResponse = await fetch('/api/user/credits/use', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${didToken}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📡 Credit response status:', creditResponse.status);
      const creditData = await creditResponse.json();
      console.log('📦 Credit response data:', creditData);

      if (!creditResponse.ok) {
        throw new Error(creditData.error || 'Failed to use credit');
      }

      setCredits(creditData.credits);
      creditUsed = true;

      // Generate image with template if selected
      console.log('🎨 Starting image generation...');
      const finalPrompt = selectedTemplate 
        ? getTemplatePrompt(
            selectedTemplate.id, 
            prompt, 
            selectedTemplate.hasAgeSlider ? drawingAge : null
          )
        : prompt;
      console.log('📝 Final prompt:', finalPrompt);
      
      const imageData = await (selectedAiModel.id === 'ideogram' 
        ? generateImage(finalPrompt)
        : generateReplicateImage(finalPrompt, selectedAiModel.id)
      );
      console.log('✅ Image generated:', imageData);
      
      // Add new image to the list with metadata
      setGeneratedImages(prevImages => [{
        url: imageData.url,
        prompt: imageData.prompt,
        originalPrompt: prompt,
        template: selectedTemplate?.name,
        timestamp: imageData.timestamp,
        userEmail: user.email
      }, ...prevImages]);
      
      setPrompt('');
    } catch (err) {
      console.error('❌ Error:', err);
      setError(err.message || "Failed to generate image. Please try again.");
      if (creditUsed && err.isGenerationError) {
        console.log('💰 Refunding credit due to generation error');
        try {
          await fetchUserCredits(user.email);
        } catch (refundError) {
          console.error('Error refunding credit:', refundError);
        }
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Memoize the download handler
  const handleDownload = useCallback(async (imageUrl, index) => {
    try {
      if (imageUrl.startsWith('data:image/svg+xml;base64,')) {
        // For SVG data URLs, create and trigger download directly in the browser
        const link = document.createElement('a');
        link.href = imageUrl;
        link.download = `ai-poster-${index + 1}.svg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // For regular URLs, use the API endpoint
        const didToken = await magic.user.getIdToken();
        const encodedUrl = encodeURIComponent(imageUrl);
        
        const response = await fetch(`/api/download?url=${encodedUrl}`, {
          headers: {
            'Authorization': `Bearer ${didToken}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to download image');
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ai-poster-${index + 1}.png`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Error downloading image:', error);
      setError('Failed to download image');
    }
  }, []);

  // Create memoized debounced handler
  const handlePromptChange = useCallback(
    debounce((value) => {
      setPrompt(value);
    }, 100),
    []
  );

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(selectedTemplate?.id === template.id ? null : template);
    
    // If selecting Line Art template, force Recraft SVG model
    if (template.id === 'line_art') {
      const recraftSvgModel = AI_MODELS.find(model => model.id === 'recraft-svg');
      setSelectedAiModel(recraftSvgModel);
    }
    // If deselecting Line Art template, reset to default Ideogram
    else if (selectedTemplate?.id === 'line_art' && template.id !== 'line_art') {
      const defaultModel = AI_MODELS.find(model => model.id === 'ideogram');
      setSelectedAiModel(defaultModel);
    }
  };

  const generateStoryPanel = async () => {
    if (!prompt.trim() || credits < 6) return;
    
    setIsGeneratingStory(true);
    setError('');
    let creditUsed = 0;
    
    try {
      // Story Structure Generation
      setStoryProgress({
        stage: 'Crafting your story...',
        progress: 10,
        currentChapter: 0,
        totalChapters: 6
      });

      const didToken = await magic.user.getIdToken();
      const storyResponse = await fetch('/api/story/generate', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${didToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ prompt })
      });

      if (!storyResponse.ok) {
        throw new Error('Failed to generate story structure');
      }

      const storyData = await storyResponse.json();
      console.log('📖 Story structure generated:', storyData);

      setStoryProgress({
        stage: 'Story written! Now creating illustrations...',
        progress: 30,
        currentChapter: 0,
        totalChapters: 6
      });

      // Generate images for each chapter
      const allPanels = [];
      
      for (let panel = 1; panel <= 6; panel++) {
        try {
          setStoryProgress({
            stage: `Illustrating Chapter ${panel}...`,
            progress: 30 + (panel * 10),
            currentChapter: panel,
            totalChapters: 6
          });

          // Use a credit
          const creditResponse = await fetch('/api/credits/use', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${didToken}`,
              'Content-Type': 'application/json'
            }
          });

          if (!creditResponse.ok) {
            throw new Error('Failed to use credit');
          }

          const creditData = await creditResponse.json();
          setCredits(creditData.credits);
          creditUsed++;

          // Generate image using the enhanced prompt
          const imageData = await generateImage(storyData.chapters[panel - 1].imagePrompt);
          
          allPanels.push({
            ...imageData,
            chapterTitle: storyData.chapters[panel - 1].title,
            storyText: storyData.chapters[panel - 1].text,
            panelNumber: panel
          });
          
          setStoryImages([...allPanels]);
        } catch (error) {
          console.error(`Error generating panel ${panel}:`, error);
          throw error;
        }
      }

      setStoryProgress({
        stage: 'Finalizing your story...',
        progress: 95,
        currentChapter: 6,
        totalChapters: 6
      });
      
      setPrompt('');
      setSuccess(`Successfully generated story: ${storyData.title}`);

      // Short delay to show completion
      setTimeout(() => {
        setStoryProgress({
          stage: 'Story complete!',
          progress: 100,
          currentChapter: 6,
          totalChapters: 6
        });
      }, 500);

    } catch (error) {
      console.error('❌ Error generating story:', error);
      setError('Failed to generate story: ' + error.message);
      
      if (creditUsed > 0) {
        try {
          await fetchUserCredits(user.email);
        } catch (refundError) {
          console.error('Error refunding credits:', refundError);
        }
      }
    } finally {
      setTimeout(() => {
        setIsGeneratingStory(false);
        setStoryProgress({
          stage: '',
          progress: 0,
          currentChapter: 0,
          totalChapters: 6
        });
      }, 1000);
    }
  };

  const renderStoryMode = () => {
    if (!selectedTemplate?.hasStoryMode) return null;

    return (
      <div className="space-y-8">
        {storyImages.map((panel, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.2 }}
            className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 shadow-lg"
          >
            <div className="flex flex-col md:flex-row gap-6">
              <div className="w-full md:w-1/2">
                <div className="relative pt-[160%] rounded-xl overflow-hidden shadow-lg">
                  <img
                    src={panel.url}
                    alt={`Story panel ${panel.panelNumber}`}
                    className="absolute inset-0 w-full h-full object-contain"
                    loading="lazy"
                  />
                </div>
              </div>
              <div className="w-full md:w-1/2 flex flex-col justify-center">
                <h3 className="text-xl font-semibold mb-2">
                  {panel.chapterTitle}
                </h3>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  {panel.storyText}
                </p>
                <button 
                  onClick={() => handleDownload(panel.url, index)}
                  className="self-start bg-white text-gray-900 px-4 py-2 rounded-full text-sm font-medium hover:bg-gray-100 transition-colors"
                >
                  Download Image
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    );
  };

  // Add this new component for the progress bar
  const StoryProgressBar = () => {
    if (!isGeneratingStory) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full mx-4">
          <div className="text-center mb-4">
            <h3 className="text-xl font-semibold mb-2">Creating Your Story</h3>
            <p className="text-gray-600">{storyProgress.stage}</p>
          </div>
          
          {/* Main Progress Bar */}
          <div className="h-3 bg-gray-200 rounded-full overflow-hidden mb-4">
            <div 
              className="h-full bg-gradient-to-r from-[#FF3366] to-[#7C3AED] transition-all duration-300"
              style={{ width: `${storyProgress.progress}%` }}
            />
          </div>

          {/* Chapter Progress */}
          {storyProgress.currentChapter > 0 && (
            <div className="flex justify-between text-sm text-gray-600">
              <span>Chapter Progress:</span>
              <span>{storyProgress.currentChapter} / {storyProgress.totalChapters}</span>
            </div>
          )}

          {/* Cancel Button */}
          <button
            onClick={() => {
              setIsGeneratingStory(false);
              setError('Story generation cancelled');
            }}
            className="mt-4 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  // Add delete handler
  const handleDeleteImage = (index) => {
    if (window.confirm('Are you sure you want to delete this image from your history?')) {
      setGeneratedImages(prevImages => {
        const newImages = [...prevImages];
        newImages.splice(index, 1);
        // Update localStorage with new array
        localStorage.setItem('generatedImages', JSON.stringify(newImages));
        return newImages;
      });
    }
  };

  const handleMenuToggle = (menuName) => {
    setMenuOpen(current => current === menuName ? null : menuName);
  };

  const handleClearImages = () => {
    if (selectedImages.length > 0) {
      // Clear only selected images
      if (window.confirm('Are you sure you want to clear the selected images from your history? This cannot be undone.')) {
        setGeneratedImages(prevImages => 
          prevImages.filter(img => !selectedImages.some(selected => selected.url === img.url))
        );
        clearSelected();
      }
    } else {
      // Clear all images
      if (window.confirm('Are you sure you want to clear all images from your history? This cannot be undone.')) {
        setGeneratedImages([]);
        localStorage.removeItem('generatedImages');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FDF7FF] to-[#F5EFFF]">
      {/* Add the progress bar component */}
      <StoryProgressBar />
      
      {isVerifyingPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-purple-500 border-t-transparent mx-auto"></div>
            <p className="mt-4 text-gray-600">Verifying your payment...</p>
          </div>
        </div>
      )}
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 md:h-20">
            <div className="flex items-center space-x-2 md:space-x-4">
              <Link to="/" className="text-xl md:text-2xl font-bold bg-gradient-to-r from-[#FF3366] to-[#7C3AED] text-transparent bg-clip-text">
                AI-to-Poster
              </Link>
              
              {/* Mobile Menu Button */}
              <button
                onClick={() => handleMenuToggle('mobile')}
                className="p-2 rounded-lg text-sm font-medium md:hidden"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                </svg>
              </button>
              
              {/* Desktop Template & AI Model Buttons */}
              <div className="hidden md:flex items-center space-x-4">
                {/* Template Button */}
                <div className="relative">
                  <button
                    onClick={() => handleMenuToggle('template')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                      selectedTemplate
                        ? 'bg-gradient-to-r from-[#FF3366] to-[#7C3AED] text-white'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                    }`}
                  >
                    {selectedTemplate ? selectedTemplate.name : 'Choose Template'}
                    <span className="ml-2">▼</span>
                  </button>
                  
                  {/* Desktop Template Menu */}
                  {menuOpen === 'template' && (
                    <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-lg py-2 z-50">
                      {templates.map((template) => (
                        <button
                          key={template.id}
                          onClick={() => {
                            handleTemplateSelect(template);
                            setMenuOpen(null);
                          }}
                          className={`w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors ${
                            selectedTemplate?.id === template.id ? 'bg-gray-50' : ''
                          }`}
                        >
                          <div className="font-medium text-gray-900">{template.name}</div>
                          <div className="text-sm text-gray-500 mt-1">{template.description}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* AI Model Button */}
                <div className="relative">
                  <button
                    onClick={() => handleMenuToggle('aiModel')}
                    disabled={selectedTemplate?.id === 'line_art'}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                      selectedTemplate?.id === 'line_art'
                        ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-700'
                        : 'bg-gradient-to-r from-[#FF3366] to-[#7C3AED] text-white'
                    }`}
                  >
                    {selectedAiModel.name}
                    <span className="ml-2">▼</span>
                  </button>
                  
                  {/* Desktop AI Model Menu */}
                  {menuOpen === 'aiModel' && selectedTemplate?.id !== 'line_art' && (
                    <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-lg py-2 z-50">
                      {AI_MODELS.map((model) => (
                        <button
                          key={model.id}
                          onClick={() => {
                            if (model.enabled || model.id === 'ideogram') {
                              setSelectedAiModel(model);
                              setMenuOpen(null);
                            }
                          }}
                          className={`w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors ${
                            selectedAiModel.id === model.id ? 'bg-gray-50' : ''
                          } ${(!model.enabled && model.id !== 'ideogram') ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <div className="font-medium text-gray-900">{model.name}</div>
                          <div className="text-sm text-gray-500 mt-1">{model.description}</div>
                          {(!model.enabled && model.id !== 'ideogram') && (
                            <span className="inline-block mt-1 text-xs px-2 py-1 bg-gray-100 rounded-full">
                              Coming Soon
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* User Info & Credits */}
            <div className="flex items-center">
              <div className="hidden md:flex items-center space-x-6">
                {cartItems.length > 0 && (
                  <button
                    onClick={() => setIsCartOpen(true)}
                    className="relative p-2 hover:bg-gray-100 rounded-full transition-colors group"
                  >
                    <svg 
                      className="w-6 h-6 text-gray-600 group-hover:text-gray-900" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" 
                      />
                    </svg>
                    <span className="absolute -top-1 -right-1 bg-gradient-to-r from-[#FF3366] to-[#7C3AED] text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                      {cartItems.length}
                    </span>
                  </button>
                )}
                <div className="text-sm">
                  <span className="text-gray-600">Credits: </span>
                  <span className="font-semibold text-gray-900">{credits}</span>
                </div>
                {plan && (
                  <div className="text-sm">
                    <span className="text-gray-600">Plan: </span>
                    <span className="font-semibold text-gray-900">{plan.name}</span>
                  </div>
                )}
                {user && (
                  <div className="text-sm text-gray-600 hidden lg:block">
                    {user.email}
                  </div>
                )}
              </div>
              
              {/* Mobile Cart Icon */}
              {cartItems.length > 0 && (
                <button
                  onClick={() => setIsCartOpen(true)}
                  className="md:hidden relative p-2 hover:bg-gray-100 rounded-full transition-colors mr-2"
                >
                  <svg 
                    className="w-6 h-6 text-gray-600" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" 
                    />
                  </svg>
                  <span className="absolute -top-1 -right-1 bg-gradient-to-r from-[#FF3366] to-[#7C3AED] text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                    {cartItems.length}
                  </span>
                </button>
              )}
              <button
                onClick={handleLogout}
                className="ml-4 text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen === 'mobile' && (
          <div className="md:hidden">
            <div className="px-4 py-2 bg-white border-t border-gray-100">
              <div className="space-y-2">
                {/* Credits & Plan Info */}
                <div className="py-2 border-b border-gray-100">
                  <div className="text-sm">
                    <span className="text-gray-600">Credits: </span>
                    <span className="font-semibold text-gray-900">{credits}</span>
                  </div>
                  {plan && (
                    <div className="text-sm mt-1">
                      <span className="text-gray-600">Plan: </span>
                      <span className="font-semibold text-gray-900">{plan.name}</span>
                    </div>
                  )}
                </div>

                {/* Template Selection Dropdown */}
                <div className="py-2 border-b border-gray-100">
                  <button
                    onClick={() => setIsTemplateDropdownOpen(!isTemplateDropdownOpen)}
                    className="w-full flex items-center justify-between p-2 text-sm font-medium text-gray-700"
                  >
                    <span>Choose Template</span>
                    <svg
                      className={`w-5 h-5 transition-transform duration-200 ${
                        isTemplateDropdownOpen ? 'transform rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {isTemplateDropdownOpen && (
                    <div className="mt-2 space-y-1 pl-2">
                      {templates.map((template) => (
                        <button
                          key={template.id}
                          onClick={() => {
                            handleTemplateSelect(template);
                            setIsTemplateDropdownOpen(false);
                            setIsTemplateMenuOpen(false);
                          }}
                          className={`w-full text-left p-2 rounded-lg text-sm transition-all duration-300 ${
                            selectedTemplate?.id === template.id
                              ? 'bg-gradient-to-r from-[#FF3366] to-[#7C3AED] text-white'
                              : 'hover:bg-gray-50'
                          }`}
                        >
                          <div className="font-medium">{template.name}</div>
                          <div className={`text-xs mt-1 ${
                            selectedTemplate?.id === template.id ? 'text-white/80' : 'text-gray-500'
                          }`}>
                            {template.description}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* AI Model Selection Dropdown */}
                <div className="py-2 border-b border-gray-100">
                  <button
                    onClick={() => setIsAiModelDropdownOpen(!isAiModelDropdownOpen)}
                    disabled={selectedTemplate?.id === 'line_art'}
                    className={`w-full flex items-center justify-between p-2 text-sm font-medium ${
                      selectedTemplate?.id === 'line_art'
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-700'
                    }`}
                  >
                    <span>Choose AI Model {selectedTemplate?.id === 'line_art' ? '(Locked to Recraft SVG)' : ''}</span>
                    {!selectedTemplate?.id === 'line_art' && (
                      <svg
                        className={`w-5 h-5 transition-transform duration-200 ${
                          isAiModelDropdownOpen ? 'transform rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </button>
                  {isAiModelDropdownOpen && !selectedTemplate?.id === 'line_art' && (
                    <div className="mt-2 space-y-1 pl-2">
                      {AI_MODELS.map((model) => (
                        <button
                          key={model.id}
                          onClick={() => {
                            if (model.enabled || model.id === 'ideogram') {
                              setSelectedAiModel(model);
                              setIsAiModelDropdownOpen(false);
                              setIsTemplateMenuOpen(false);
                            }
                          }}
                          className={`w-full text-left p-2 rounded-lg text-sm transition-all duration-300 ${
                            selectedAiModel.id === model.id
                              ? 'bg-gradient-to-r from-[#FF3366] to-[#7C3AED] text-white'
                              : 'hover:bg-gray-50'
                          } ${(!model.enabled && model.id !== 'ideogram') ? 'opacity-50' : ''}`}
                        >
                          <div className="font-medium">{model.name}</div>
                          <div className={`text-xs mt-1 ${
                            selectedAiModel.id === model.id ? 'text-white/80' : 'text-gray-500'
                          }`}>
                            {model.description}
                          </div>
                          {(!model.enabled && model.id !== 'ideogram') && (
                            <span className="inline-block mt-1 text-xs px-2 py-1 bg-gray-100 rounded-full">
                              Coming Soon
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 pt-32 pb-20">
        {/* Credit Warning */}
        {credits <= 5 && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-4 bg-yellow-50 border border-yellow-200 rounded-xl text-center"
          >
            <p className="text-yellow-800">
              You're running low on credits! Only {credits} generations remaining.{' '}
              <Link to="/pricing" className="underline font-medium">
                Upgrade your plan
              </Link>{' '}
              to continue creating amazing posters.
            </p>
          </motion.div>
        )}

        {selectedTemplate?.hasStoryMode && (
          <div className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-xl">
            <p className="text-purple-800">
              Story Mode will generate a 6-panel illustrated story. This will use 6 credits.
              {credits < 6 && " You need more credits to generate a story."}
            </p>
          </div>
        )}

        {/* Prompt Input Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="max-w-3xl mx-auto"
        >
          <h1 className="text-3xl md:text-4xl font-bold text-center mb-8 bg-gradient-to-r from-[#FF3366] to-[#7C3AED] text-transparent bg-clip-text">
            Create Your AI Poster
          </h1>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <textarea
                value={debouncedPrompt}
                onChange={(e) => {
                  const value = e.target.value;
                  setDebouncedPrompt(value);
                  handlePromptChange(value);
                }}
                placeholder={selectedTemplate 
                  ? selectedTemplate.placeholder 
                  : "Describe your perfect poster..."}
                className="w-full p-4 h-32 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all resize-none bg-white/60 backdrop-blur-sm"
              />
              <button
                type="submit"
                disabled={isGenerating || !prompt.trim() || credits <= 0}
                className="w-full md:w-auto mt-2 md:mt-0 md:absolute md:bottom-4 md:right-4 px-6 py-2 bg-gradient-to-r from-[#FF3366] to-[#7C3AED] text-white rounded-full hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGenerating ? 'Generating...' : `Generate (${credits} left)`}
              </button>
            </div>
          </form>
        </motion.div>

        {/* Generated Images Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className={`${
            selectedTemplate ? 'mt-16' : 'mt-32'
          }`}
        >
          {/* Warning message with buttons */}
          <div className="container mx-auto px-4 mb-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              {/* Left side - Add to Cart button */}
              <div className="flex-shrink-0 order-2 md:order-1">
                {selectedImages.length > 0 && (
                  <button
                    onClick={() => {
                      selectedImages.forEach(image => addToCart(image));
                      clearSelected();
                    }}
                    className="px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 
                      bg-gradient-to-r from-[#FF3366] to-[#7C3AED] text-white hover:shadow-lg 
                      hover:-translate-y-0.5 active:translate-y-0 flex items-center gap-2"
                  >
                    <span>🛒</span>
                    Add Selected
                  </button>
                )}
              </div>

              {/* Center text */}
              <p className="text-gray-500 text-sm text-center flex-1 order-1 md:order-2">
                ⚠️ Images created by AI-to-poster are not stored and will expire. You must download and store it.
              </p>

              {/* Right side - Clear button */}
              <div className="flex-shrink-0 order-3">
                {generatedImages.length > 0 && (
                  <button
                    onClick={handleClearImages}
                    className="px-4 py-2 rounded-full text-sm font-medium transition-all duration-300
                      border border-red-500 text-red-500 hover:bg-red-50 
                      hover:-translate-y-0.5 active:translate-y-0"
                  >
                    {selectedImages.length > 0 ? 'Clear Selected' : 'Clear All'}
                  </button>
                )}
              </div>
            </div>
          </div>

          {isGenerating && (
            <div className="text-center mb-8">
              <div className="inline-block p-4 rounded-full bg-white/60 backdrop-blur-sm shadow-lg">
                <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {generatedImages.map((image, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative group"
              >
                {/* Selection checkbox - now only visible on hover */}
                <div className="absolute left-3 top-3 z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleImageSelection(image);
                    }}
                    className={`w-6 h-6 rounded-full border-2 transition-all duration-300 ${
                      selectedImages.some(img => img.url === image.url)
                        ? 'bg-purple-500 border-purple-500'
                        : 'border-white bg-white/20 backdrop-blur-sm'
                    } flex items-center justify-center`}
                  >
                    {selectedImages.some(img => img.url === image.url) && (
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                </div>

                {/* Delete button - restored */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteImage(index);
                  }}
                  className="absolute top-2 right-2 p-2 bg-red-500 hover:bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-75 hover:opacity-100 transition-all duration-300 shadow-lg z-20"
                  title="Delete image"
                >
                  <svg 
                    className="w-4 h-4" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>

                <div className="relative pt-[160%] rounded-xl overflow-hidden shadow-lg group-hover:shadow-xl transition-all duration-300 bg-white/60">
                  <img
                    src={image.url}
                    alt={`Generated poster ${index + 1}`}
                    className={`absolute inset-0 w-full h-full ${
                      image.isSvg 
                        ? 'object-contain bg-white p-4'
                        : 'object-contain'
                    }`}
                    loading="lazy"
                    onError={(e) => {
                      console.error('❌ Image load error:', e);
                      e.target.onerror = null;
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJjdXJyZW50Q29sb3IiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBjbGFzcz0iZmVhdGhlciBmZWF0aGVyLWltYWdlIj48cmVjdCB4PSIzIiB5PSIzIiB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHJ4PSIyIiByeT0iMiI+PC9yZWN0PjxjaXJjbGUgY3g9IjguNSIgY3k9IjguNSIgcj0iMS41Ij48L2NpcmNsZT48cG9seWxpbmUgcG9pbnRzPSIyMSAxNSAxNiAxMCA1IDIxIj48L3BvbHlsaW5lPjwvc3ZnPg==';
                    }}
                  />
                </div>

                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl flex flex-col items-center justify-end p-4">
                  {/* Add to Cart button */}
                  <button
                    onClick={() => addToCart(image)}
                    className="w-full bg-white text-gray-900 px-4 py-2 rounded-full text-sm font-medium hover:bg-gray-100 transition-colors mb-2 flex items-center justify-center"
                  >
                    <span className="mr-2">🛒</span> Add to Cart
                  </button>

                  {/* Download button */}
                  <button 
                    onClick={() => handleDownload(image.url, index)}
                    className="w-full bg-white text-gray-900 px-4 py-2 rounded-full text-sm font-medium hover:bg-gray-100 transition-colors mb-4"
                  >
                    Download
                  </button>

                  {/* Restored image description with show more feature */}
                  <div className="relative w-full">
                    {image.template && (
                      <p className="text-white/80 text-xs mb-2">
                        Template: {image.template}
                      </p>
                    )}
                    <p className={`text-white text-sm mb-2 text-center ${
                      expandedPrompt === index ? '' : 'line-clamp-2'
                    }`}>
                      {image.prompt}
                    </p>
                    {image.prompt.length > 100 && (
                      <button
                        onClick={() => setExpandedPrompt(expandedPrompt === index ? null : index)}
                        className="text-white/70 text-xs hover:text-white transition-colors mt-1"
                      >
                        {expandedPrompt === index ? 'Show less' : 'Show more'}
                      </button>
                    )}
                  </div>
                  <p className="text-white/70 text-xs">
                    {new Date(image.timestamp).toLocaleString()}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Decorative Elements */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none -z-10">
          <div className="absolute top-[-5%] right-[-5%] w-1/3 h-1/3 bg-gradient-to-br from-purple-200 to-pink-200 rounded-full blur-3xl opacity-30"></div>
          <div className="absolute bottom-[-5%] left-[-5%] w-1/3 h-1/3 bg-gradient-to-tr from-blue-200 to-teal-200 rounded-full blur-3xl opacity-30"></div>
        </div>
      </div>

      {/* Add Cart Drawer */}
      <CartDrawer 
        isOpen={isCartOpen} 
        onClose={() => setIsCartOpen(false)} 
      />
    </div>
  );
};

export default Generator;
